import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Container, Box, Paper, Grid, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Alert, CircularProgress, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { PriceChange, CloudUpload, CheckCircle, SyncAlt } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import TituloPagina from './utils/TituloPagina.jsx';
import Cargando from './utils/Cargando.jsx';
import TildeExito from './utils/TildeExito.jsx';
import { reproducirSonidoExito } from '../services/sonido';
import { API_BASE_URL, PRECIOS_SUBIR, PRECIOS_PREVISUALIZAR, PRECIOS_APLICAR } from '../services/apiConfig';
import { limpiarProductosCache } from '../services/productosCache';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Recuerda el Excel ya subido (token + hoja) mientras dure la pestaña, para
// no obligar a resubirlo si el usuario se va a arreglar el mapeo y vuelve —
// al volver, se re-controla solo contra el archivo temporal del servidor
// (vive ~2hs ahí). Si ya no está, se limpia y se pide subir de nuevo.
const CLAVE_ESTADO = 'precios_import_pendiente';
const guardarEstado = (s) => { try { sessionStorage.setItem(CLAVE_ESTADO, JSON.stringify(s)); } catch { /* noop */ } };
const leerEstadoGuardado = () => { try { return JSON.parse(sessionStorage.getItem(CLAVE_ESTADO) || 'null'); } catch { return null; } };
const limpiarEstadoGuardado = () => { try { sessionStorage.removeItem(CLAVE_ESTADO); } catch { /* noop */ } };

function PreciosImportar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileRef = useRef(null);

  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [hojas, setHojas] = useState([]);
  const [hoja, setHoja] = useState('');

  const [cargandoDiff, setCargandoDiff] = useState(false);
  const [diff, setDiff] = useState(null);

  const [confirmarOpen, setConfirmarOpen] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [mostrarExito, setMostrarExito] = useState(false);

  const reiniciar = () => {
    setToken(null); setNombreArchivo(''); setHojas([]); setHoja('');
    setDiff(null); setResultado(null); setError('');
    limpiarEstadoGuardado();
    if (fileRef.current) fileRef.current.value = '';
  };

  const previsualizar = async (tok, hojaElegida) => {
    setCargandoDiff(true);
    setError('');
    try {
      const res = await axios.get(API_BASE_URL + PRECIOS_PREVISUALIZAR, {
        params: { token: tok, sheet: hojaElegida },
        headers: authHeader(),
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al leer la hoja.');
      setDiff(res.data?.data ?? null);
      return true;
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al leer la hoja.');
      setDiff(null);
      return false;
    } finally {
      setCargandoDiff(false);
    }
  };

  // Al entrar a la pantalla, si hay un Excel ya subido en esta pestaña (se
  // guardó al subirlo/cambiar de hoja), se re-controla solo en vez de pedir
  // que lo vuelvan a subir. Si el archivo temporal ya no está en el servidor,
  // se limpia y queda la pantalla en blanco para subir de nuevo.
  useEffect(() => {
    const guardado = leerEstadoGuardado();
    if (!guardado?.token || !guardado?.hoja) return;
    setToken(guardado.token);
    setNombreArchivo(guardado.nombreArchivo || '');
    setHojas(guardado.hojas || []);
    setHoja(guardado.hoja);
    previsualizar(guardado.token, guardado.hoja).then((ok) => { if (!ok) limpiarEstadoGuardado(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onElegirArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reiniciar();
    setNombreArchivo(file.name);
    setSubiendo(true);
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const res = await axios.post(API_BASE_URL + PRECIOS_SUBIR, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data;
      if (!res.data.success || !data) throw new Error(res.data.message || 'Error al subir el archivo.');
      setToken(data.token);
      setHojas(data.hojas || []);
      const inicial = data.hoja_sugerida || data.hojas?.[0] || '';
      setHoja(inicial);
      guardarEstado({ token: data.token, nombreArchivo: file.name, hojas: data.hojas || [], hoja: inicial });
      if (inicial) await previsualizar(data.token, inicial);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al subir el archivo.');
    } finally {
      setSubiendo(false);
    }
  };

  const onCambiarHoja = (nuevaHoja) => {
    setHoja(nuevaHoja);
    guardarEstado({ token, nombreArchivo, hojas, hoja: nuevaHoja });
    if (token) previsualizar(token, nuevaHoja);
  };

  const aplicarCambios = async () => {
    setAplicando(true);
    setError('');
    try {
      const res = await axios.post(API_BASE_URL + PRECIOS_APLICAR, { token, sheet: hoja }, { headers: authHeader() });
      if (!res.data.success) throw new Error(res.data.message);
      setResultado(res.data.data);
      setConfirmarOpen(false);
      setDiff(null);
      setMostrarExito(true);
      // El archivo temporal ya se borró en el servidor al aplicar: no tiene
      // sentido seguir recordándolo para la próxima vez que se entre acá.
      limpiarEstadoGuardado();
      // Los precios cambiaron: que Registrar no siga mostrando los viejos
      // desde la caché de esta pestaña hasta que alguien la reabra.
      limpiarProductosCache();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al aplicar los cambios.');
      setConfirmarOpen(false);
    } finally {
      setAplicando(false);
    }
  };

  const cambios = useMemo(() => diff?.cambios ?? [], [diff]);
  const sinMapear = diff?.sin_mapear ?? [];
  const sinProducto = diff?.sin_producto ?? [];
  const sinCambios = diff?.sin_cambios ?? [];

  // Agrupados por categoría (orden de primera aparición) para que la tabla
  // se lea más fácil que una lista plana de 60+ filas.
  const gruposCambios = useMemo(() => {
    const mapa = new Map();
    cambios.forEach((c) => {
      const cat = c.categoria || 'Sin categoría';
      if (!mapa.has(cat)) mapa.set(cat, []);
      mapa.get(cat).push(c);
    });
    return Array.from(mapa, ([categoria, items]) => ({ categoria, items }));
  }, [cambios]);

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <TildeExito
        submitting={aplicando}
        mostrar={mostrarExito}
        onFinalizado={() => setMostrarExito(false)}
        sonido={reproducirSonidoExito}
        mensaje={resultado ? `${resultado.actualizados} precio(s) actualizados` : 'Precios actualizados'}
      />
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={PriceChange}
          titulo="Actualizar precios"
          subtitulo="Subí la lista de precios en Excel y revisá los cambios antes de aplicarlos"
          ayuda="Cruza cada línea del Excel contra el mapeo de productos ya cargado. Un producto no reconocido no se actualiza — hay que agregarlo primero al mapeo. No se aplica nada hasta que confirmes."
          accion={<Button variant="outlined" startIcon={<SyncAlt />} onClick={() => navigate('/mapeo-precios')}>Ver mapeo de productos</Button>}
        />

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onElegirArchivo} />
            <Button
              variant="contained" startIcon={subiendo ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
              onClick={() => fileRef.current?.click()} disabled={subiendo}
            >
              {subiendo ? 'Subiendo…' : 'Subir Excel'}
            </Button>
            {nombreArchivo && <Typography variant="body2" color="text.secondary">{nombreArchivo}</Typography>}

            {hojas.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Hoja</InputLabel>
                <Select label="Hoja" value={hoja} onChange={(e) => onCambiarHoja(e.target.value)}>
                  {hojas.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Se cruza cada línea de la hoja elegida contra el mapeo de productos ya cargado en el sistema.
          </Typography>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {resultado && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }} onClose={reiniciar}>
            Se actualizaron {resultado.actualizados} precio(s). ({resultado.sin_mapear} sin mapear, {resultado.sin_producto} sin producto — no se tocaron)
          </Alert>
        )}

        {cargandoDiff ? (
          <Cargando inline />
        ) : diff && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{cambios.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Con cambio de precio</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{sinCambios.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Sin cambios</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: sinMapear.length ? 'warning.main' : 'text.primary' }}>{sinMapear.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Sin mapear</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: sinProducto.length ? 'error.main' : 'text.primary' }}>{sinProducto.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Mapeadas sin producto</Typography>
                </Paper>
              </Grid>
            </Grid>

            {sinMapear.length > 0 && (
              <Alert
                severity="warning" sx={{ mb: 2 }}
                action={<Button color="inherit" size="small" onClick={() => navigate('/mapeo-precios')}>Agregar al mapeo</Button>}
              >
                {sinMapear.length} línea(s) del Excel no se reconocen (no están en el mapeo de productos): {sinMapear.map((s) => `"${s.nombre}"`).join(', ')}.
              </Alert>
            )}
            {sinProducto.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {sinProducto.length} línea(s) están mapeadas pero el producto no existe o está inactivo: {sinProducto.map((s) => `"${s.nombre}"`).join(', ')}.
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Cambios de precio</Typography>
                <Button
                  variant="contained" color="primary" disabled={cambios.length === 0}
                  onClick={() => setConfirmarOpen(true)}
                >
                  Aplicar {cambios.length} cambio{cambios.length !== 1 ? 's' : ''}
                </Button>
              </Box>
              {cambios.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay cambios de precio en esta hoja
                  {sinCambios.length > 0 && ` (se encontraron ${sinCambios.length} producto${sinCambios.length !== 1 ? 's' : ''} que ya tenía${sinCambios.length !== 1 ? 'n' : ''} el precio del Excel)`}.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {gruposCambios.map(({ categoria, items }) => (
                    <Box key={categoria} sx={{ overflowX: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{categoria}</Typography>
                        <Chip size="small" label={items.length} variant="outlined" />
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell align="right">Precio actual</TableCell>
                            <TableCell align="right">Precio nuevo</TableCell>
                            <TableCell align="right">Diferencia</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((c) => (
                            <TableRow key={c.producto_id}>
                              <TableCell>{c.descripcion}</TableCell>
                              <TableCell align="right">{fmtMoneda(c.precio_actual)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoneda(c.precio_nuevo)}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ color: c.diferencia >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                                  {c.diferencia >= 0 ? '+' : ''}{fmtMoneda(c.diferencia)} ({c.diferencia_pct != null ? `${c.diferencia_pct}%` : '—'})
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>

      <Dialog open={confirmarOpen} onClose={() => !aplicando && setConfirmarOpen(false)}>
        <DialogTitle>Aplicar cambios de precio</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se van a actualizar {cambios.length} producto{cambios.length !== 1 ? 's' : ''} con el nuevo precio del Excel. Esta acción no se puede deshacer desde acá.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarOpen(false)} disabled={aplicando}>Cancelar</Button>
          <Button
            variant="contained" color="primary" onClick={aplicarCambios} disabled={aplicando}
            startIcon={aplicando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {aplicando ? 'Aplicando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PreciosImportar;
